import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Calendar, Users, TrendingUp, Shield, Bell, FileText, BarChart3, Globe, CheckCircle, ArrowRight } from 'lucide-react';

const features = [
  {
    icon: Calendar,
    title: "Leave Request Management",
    description: "Teachers can apply for leave in seconds. Select leave type, dates, add reason, and submit. Track status in real-time.",
    highlights: ["Sick, Casual, Vacation & custom types", "Date picker with calendar view", "Document upload support"],
    color: 'teal'
  },
  {
    icon: Users,
    title: "Staff Management",
    description: "Add teachers and HR staff with roles and departments. Bulk import via CSV for faster onboarding.",
    highlights: ["Role-based access (Admin, HR, Teacher)", "Department organization", "Temporary password generation"],
    color: 'blue'
  },
  {
    icon: CheckCircle,
    title: "One-Click Approvals",
    description: "Admins and HR can review, approve or reject leave requests from a centralized dashboard with full context.",
    highlights: ["View leave balance before approving", "Add comments on approval/rejection", "Batch processing support"],
    color: 'emerald'
  },
  {
    icon: TrendingUp,
    title: "Leave Balance Tracking",
    description: "Automatic tracking of allocated, used, pending and available leave days. Carry-forward policies supported.",
    highlights: ["Real-time balance updates", "Year-wise tracking", "Policy-based allocation"],
    color: 'amber'
  },
  {
    icon: BarChart3,
    title: "Analytics & Reports",
    description: "Comprehensive dashboards showing leave trends, department-wise utilization, and staff availability patterns.",
    highlights: ["Monthly/Quarterly trends", "Department-wise reports", "Export to PDF, Excel, CSV"],
    color: 'indigo'
  },
  {
    icon: Bell,
    title: "In-App Notifications",
    description: "Stay updated with instant notifications for new requests, approvals, rejections, and important updates.",
    highlights: ["Real-time notification bell", "Mark as read / read all", "Linked to relevant pages"],
    color: 'rose'
  },
  {
    icon: Shield,
    title: "Security & Compliance",
    description: "Enterprise-grade security with encrypted data, JWT authentication, and complete tenant isolation.",
    highlights: ["AES-256 encryption at rest", "TLS 1.3 in transit", "GDPR & FERPA ready"],
    color: 'slate'
  },
  {
    icon: Globe,
    title: "Multi-Board Support",
    description: "Designed for Indian schools across all boards — CBSE, ICSE, State Boards, IB, and more.",
    highlights: ["State & district-wise setup", "All Indian states & UTs", "Localized for Indian education"],
    color: 'cyan'
  }
];

const colorMap = {
  teal: { bg: 'bg-teal-50', icon: 'text-teal-600', ring: 'group-hover:ring-teal-200' },
  blue: { bg: 'bg-blue-50', icon: 'text-blue-600', ring: 'group-hover:ring-blue-200' },
  emerald: { bg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'group-hover:ring-emerald-200' },
  amber: { bg: 'bg-amber-50', icon: 'text-amber-600', ring: 'group-hover:ring-amber-200' },
  indigo: { bg: 'bg-indigo-50', icon: 'text-indigo-600', ring: 'group-hover:ring-indigo-200' },
  rose: { bg: 'bg-rose-50', icon: 'text-rose-600', ring: 'group-hover:ring-rose-200' },
  slate: { bg: 'bg-slate-100', icon: 'text-slate-600', ring: 'group-hover:ring-slate-200' },
  cyan: { bg: 'bg-cyan-50', icon: 'text-cyan-600', ring: 'group-hover:ring-cyan-200' },
};

export const Features = () => {
  const navigate = useNavigate();

  return (
    <>
      <Header />
      <div className="min-h-screen bg-white" data-testid="features-page">
        {/* Hero */}
        <section className="relative py-24 px-6 bg-slate-900 overflow-hidden">
          <div className="absolute top-[-100px] right-[20%] w-[500px] h-[500px] rounded-full bg-teal-500/10 blur-[120px] pointer-events-none" />
          <div className="max-w-7xl mx-auto relative z-10">
            <div className="max-w-2xl">
              <p className="text-teal-400 text-sm font-semibold uppercase tracking-wider mb-4">Platform Features</p>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-6 leading-tight" data-testid="features-title">
                Everything your school needs
              </h1>
              <p className="text-lg text-slate-400 max-w-xl mb-8">
                A complete leave management platform built specifically for Indian schools. Simple for teachers, powerful for administrators.
              </p>
              <Button
                size="lg"
                className="h-13 px-8 bg-teal-500 hover:bg-teal-400 text-white font-semibold rounded-full"
                onClick={() => navigate('/register')}
                data-testid="features-cta-btn"
              >
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-24 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {features.map((feature, idx) => {
                const Icon = feature.icon;
                const c = colorMap[feature.color];
                return (
                  <div
                    key={idx}
                    className="group p-6 rounded-2xl border border-slate-200 hover:shadow-xl hover:border-slate-300 transition-all duration-300 ring-2 ring-transparent"
                    data-testid={`feature-detail-${idx}`}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`h-12 w-12 rounded-xl ${c.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`h-6 w-6 ${c.icon}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                        <p className="text-sm text-slate-500 mb-4 leading-relaxed">{feature.description}</p>
                        <ul className="space-y-1.5">
                          {feature.highlights.map((h, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                              <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0" />
                              {h}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="py-20 px-6 bg-slate-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Ready to get started?</h2>
            <p className="text-slate-500 mb-8">Join thousands of Indian schools already using Ariskool.</p>
            <Button
              size="lg"
              className="h-13 px-10 bg-teal-600 hover:bg-teal-500 text-white rounded-full font-semibold"
              onClick={() => navigate('/register')}
              data-testid="features-bottom-cta"
            >
              Create Free Account <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Features;
